import { useEffect } from 'react';
import { readableOn } from '../lib/contrast';
import type { Branding } from '../lib/types';

/**
 * Applies a tenant's identity to the live document: colours as CSS custom
 * properties, the tab title, and the favicon. Doing it at runtime rather than
 * at build time is what makes one deployment serve every instructor's brand.
 */
export function useApplyBranding(branding: Branding | null) {
  useEffect(() => {
    const root = document.documentElement;
    // Target's own red, as tokens.css has it, where no centre has spoken.
    const primary = branding?.primaryColor ?? '#cf1a2b';
    const accent = branding?.accentColor ?? '#0f766e';

    root.style.setProperty('--brand', primary);
    root.style.setProperty('--brand-accent', accent);
    // Recomputed here, not stored: an instructor can change --brand at any time
    // and text painted on it has to stay legible without a second round-trip.
    root.style.setProperty('--on-brand', readableOn(primary));

    document.title = branding ? `${branding.name} — Target` : 'Target';

    // A centre's own favicon where it has one, Target's mark otherwise — set
    // both ways, so leaving a centre's page does not keep its icon in the tab.
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    const href = branding?.faviconUrl ?? '/favicon.svg';
    if (link.getAttribute('href') !== href) {
      link.removeAttribute('type');
      link.href = href;
    }
  }, [branding]);
}
